import { useState } from 'react'
import useUsers from '../../hooks/useUsers'

/**
 * Componente de búsqueda y asignación de usuarios para tickets.
 * * **Funcionalidades**:
 * - Autocompletado en tiempo real basado en nombre y apellido.
 * - Menú desplegable dinámico con fotos de perfil o avatares por iniciales.
 * - Integración con el hook `useUsers` para obtener el listado global de usuarios.
 * - Manejo de estados de enfoque (focus) y desenfoque (blur) con retardo para permitir clics.
 * * @component
 * @param {Object} props - Propiedades del componente.
 * @param {Function} props.assign - Callback que se ejecuta al seleccionar un usuario.
 * Recibe el objeto `user` seleccionado como argumento.
 * * @returns {JSX.Element} Un buscador interactivo con lista desplegable de resultados.
 */
/**
 * Devuelve el nombre completo del usuario tolerando campos nulos o ausentes.
 * @param {Object} user - El objeto de usuario.
 * @returns {string} Nombre y apellido concatenados, sin espacios sobrantes.
 */
const fullName = (user) =>
  `${user?.nombre ?? ''} ${user?.apellido ?? ''}`.trim()

/**
 * Construye las iniciales del usuario a partir de nombre y apellido.
 * @param {Object} user - El objeto de usuario.
 * @returns {string} Iniciales en mayúscula, o '?' si no hay datos.
 */
const initials = (user) => {
  const letters = `${user?.nombre?.[0] ?? ''}${user?.apellido?.[0] ?? ''}`
  return letters.toUpperCase() || '?'
}

const AssignUserTicket = ({ assign }) => {
  /** @type {Object} Lista de todos los usuarios disponibles obtenida mediante hook. */
  const { allUsers } = useUsers()

  /** @type {[string, Function]} Estado local para el término de búsqueda actual. */
  const [searchTerm, setSearchTerm] = useState('')

  /** @type {[boolean, Function]} Estado para controlar la visibilidad del dropdown. */
  const [isOpen, setIsOpen] = useState(false)

  /**
   * Lista de usuarios filtrados según el término de búsqueda.
   * Filtra por la concatenación de nombre y apellido.
   */
  const filteredUsers = (allUsers ?? []).filter((user) =>
    fullName(user).toLowerCase().includes(searchTerm.toLowerCase())
  )

  /**
   * Gestiona la selección de un usuario de la lista.
   * Actualiza el input, cierra el menú y notifica al componente padre.
   * @param {Object} user - El objeto de usuario seleccionado.
   */
  const handleSelect = (user) => {
    setSearchTerm(fullName(user))
    setIsOpen(false)
    if (assign) assign(user)
  }

  return (
    // El contenedor principal usa clases de Bootstrap que responden al tema
    <div
      className="position-relative w-100 shadow-sm rounded-pill"
      style={{ maxWidth: '350px' }}
    >
      {/* Contenedor del Buscador */}
      <div className="input-group overflow-hidden rounded-pill border shadow-sm bg-body">
        <span className="input-group-text bg-transparent border-0 pe-2 ps-3">
          <i className="bi bi-search text-secondary"></i>
        </span>
        <input
          type="text"
          className="form-control border-0 shadow-none bg-transparent text-body"
          placeholder="Buscar para asignar..."
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value)
            setIsOpen(true)
          }}
          onFocus={() => setIsOpen(true)}
          // Cerramos el menú con un pequeño delay para permitir el click en la lista
          onBlur={() => setTimeout(() => setIsOpen(false), 200)}
        />
      </div>

      {/* Lista Desplegable (Dropdown) */}
      {isOpen && searchTerm.length > 0 && (
        <ul
          className="list-group position-absolute w-100 mt-2 shadow-lg border-0 rounded-3 overflow-hidden"
          style={{ zIndex: 1050, maxHeight: '250px', overflowY: 'auto' }}
        >
          {filteredUsers.length > 0 ? (
            filteredUsers.map((user) => (
              <li
                key={user.id}
                className="list-group-item list-group-item-action d-flex align-items-center border-0 bg-body-tertiary text-body"
                style={{ cursor: 'pointer', gap: '12px', padding: '10px 15px' }}
                onClick={() => handleSelect(user)}
              >
                {/* Foto o Avatar */}
                {user.photo ? (
                  <img
                    src={user.photo}
                    alt="perfil"
                    className="rounded-circle border border-2 border-primary"
                    style={{
                      width: '35px',
                      height: '35px',
                      objectFit: 'cover',
                    }}
                  />
                ) : (
                  <div
                    className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center fw-bold"
                    style={{ width: '35px', height: '35px', fontSize: '11px' }}
                  >
                    {initials(user)}
                  </div>
                )}

                <div className="overflow-hidden">
                  <div className="fw-bold small text-truncate">
                    {fullName(user) || 'Sin nombre'}
                  </div>
                  <div
                    className="text-muted text-truncate"
                    style={{ fontSize: '11px' }}
                  >
                    {user.email}
                  </div>
                </div>
              </li>
            ))
          ) : (
            <li className="list-group-item bg-body text-muted small text-center p-3">
              No hay coincidencias
            </li>
          )}
        </ul>
      )}
    </div>
  )
}

export default AssignUserTicket
